import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAssessmentStore } from '../../store/assessment.store';
import type { Profile } from '../../types';
import type { Result } from '../../types/assessment.types';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';

export const ResultSimulator: React.FC = () => {
  const navigate = useNavigate();
  const { actions } = useAssessmentStore();
  const setResults = actions.setResult;

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [selectedTier, setSelectedTier] = useState<'free' | 'premium'>('premium');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiService.getProfiles()
      .then(data => {
        if (data && data.length > 0) {
          setProfiles(data);
          setSelectedProfile(data[0].id);
        }
      })
      .catch(() => setError('Failed to load profiles.'));
  }, []);

  const handleSimulate = async () => {
    if (!selectedProfile) {
      setError('Please select a profile.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const results = await apiService.simulateResult(selectedProfile, selectedTier);
      setResults(results);
      navigate('/results/view');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred.');
    } finally {
      setIsLoading(false);
    }
  };

  const commonInputStyle = "w-full p-2 border rounded-md bg-white";
  const commonLabelStyle = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-2xl">
      <Card>
        <div className="p-8">
          <h1 className="text-2xl font-bold mb-2">Assessment Result Simulator</h1>
          <p className="text-gray-600 mb-6">
            Select a profile and a subscription tier to generate a simulated result.
          </p>
          
          <div className="space-y-6">
            <div>
              <label htmlFor="profile-select" className={commonLabelStyle}>Profile</label>
              <select
                id="profile-select"
                value={selectedProfile}
                onChange={(e) => setSelectedProfile(e.target.value)}
                className={commonInputStyle}
              >
                {profiles.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>

            <div>
              <span className={commonLabelStyle}>Subscription Tier</span>
              <div className="flex space-x-4 mt-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="tier"
                    value="premium"
                    checked={selectedTier === 'premium'}
                    onChange={() => setSelectedTier('premium')}
                    className="form-radio"
                  />
                  <span>Premium</span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="tier"
                    value="free"
                    checked={selectedTier === 'free'}
                    onChange={() => setSelectedTier('free')}
                    className="form-radio"
                  />
                  <span>Free</span>
                </label>
              </div>
            </div>

            <Button onClick={handleSimulate} disabled={isLoading || profiles.length === 0} className="w-full">
              {isLoading ? 'Simulating...' : `Simulate Result`}
            </Button>

            {error && <p className="text-red-500 text-sm mt-4">{error}</p>}
          </div>
        </div>
      </Card>
    </div>
  );
}; 